import React from 'react';

import { useFormContext } from 'react-hook-form';
import { useSelector, useStore } from 'react-redux';

import { addMonths, format } from 'date-fns';
import PropTypes from 'prop-types';
import { v4 as uuid } from 'uuid';

import Form, { AutoSave } from '@asteria/component-form';

import * as AppStore from '@asteria/datalayer/stores/app';
import * as ScenarioStore from '@asteria/datalayer/stores/scenarios';

import { useTranslate } from '@asteria/language';
import { findParentByClassname } from '@asteria/utils-funcs/node';

import ItemView from './view';

const ForecasterTransactionConfig = {
	$customer: {
		type: 'select',
		name: 'descriptionClient',
		dataSource: 'app.clients',
		filters: { type: 'CUSTOMER' },
		linkType: 'CUSTOMER',
	},
	$supplier: {
		type: 'select',
		name: 'descriptionClient',
		dataSource: 'app.clients',
		filters: { type: 'SUPPLIER' },
		linkType: 'SUPPLIER',
	},
};

const TransactionItemProvider = React.memo(
	(props) => {
		const {
			className,
			operation,
			onAction,
			showCategory,
			showTags,
			variant,
		} = props;
		const { type, tags } = operation?.payload ?? {};
		const { getValues, setValue } = useFormContext();
		const store = useStore();
		const currencySelectorRef = React.useRef(null);
		const { t } = useTranslate();
		const customerTag = useSelector((store) =>
			AppStore.selectors
				.tags(store)
				.find((tag) => tag.name === '$customer'),
		);
		const supplierTag = useSelector((store) =>
			AppStore.selectors
				.tags(store)
				.find((tag) => tag.name === '$supplier'),
		);

		const [removed, setRemoved] = React.useState(false);

		const handleRemove = React.useCallback(() => {
			setRemoved(true);

			const changes = getValues('changes') ?? [];
			setValue('changes', [
				...changes,
				{
					op: 'REMOVE',
					uuid: uuid(),
					payload: {
						targetId: operation.uuid ?? operation._id,
					},
				},
			]);
		}, [operation, getValues, setValue]);

		const handleCopy = React.useCallback(() => {
			const newOperation = {
				op: 'CREATE',
				uuid: uuid(),
				payload: {
					...operation.payload,
					paymentDate: format(
						addMonths(new Date(operation.payload.paymentDate), 1),
						'yyyy-MM-dd',
					),
					targetId: null,
				},
			};

			const changes = getValues('changes') ?? [];
			const allOperations = getValues('allOperations') ?? [];

			setValue('changes', [...changes, newOperation]);
			setValue('allOperations', [...allOperations, newOperation]);
		}, [operation, getValues, setValue]);

		const inputConfig = React.useMemo(() => {
			if (tags.includes(customerTag._id)) {
				return ForecasterTransactionConfig.$customer;
			}

			if (tags.includes(supplierTag._id)) {
				return ForecasterTransactionConfig.$supplier;
			}

			return null;
		}, [supplierTag, customerTag, tags]);

		const placeholder = React.useMemo(() => {
			if (inputConfig) {
				return null;
			}

			const path = name.split('.');
			const category = path?.[5]?.replace('$', '');
			const tag = path?.[7]?.replace('$', '');

			return t(
				[
					'forecast.transactions.content.item.field.description',
					'forecast.transactions.content.item.field.description.default',
				],
				{
					category: category,
					tag: tag,
				},
			);
		}, [inputConfig, t]);

		const InputFormat = React.useMemo(() => ({ valueAsNumber: true }), []);

		const [$style, setStyle] = React.useState({});

		React.useLayoutEffect(() => {
			const timeout = setTimeout(() => {
				if (currencySelectorRef.current) {
					const node = findParentByClassname(
						currencySelectorRef.current,
						'asteria-component__select',
					);

					const rect = node.getBoundingClientRect();

					setStyle({ '--default-currency-width': `${rect.width}px` });
				}
			}, 500);

			return () => {
				clearTimeout(timeout);
			};
		}, [operation]);

		const style = React.useMemo(() => $style, [$style]);

		const handleSubmit = React.useCallback(
			async (data) => {
				const sourceScenarioData =
					ScenarioStore.selectors.sourceScenarioData(
						store.getState(),
					);

				if (inputConfig && data?.$extra?.[inputConfig.name]) {
					data.payload.links = [
						{
							type: inputConfig.linkType,
							id: data?.$extra?.[inputConfig.name],
						},
					];
				}

				delete data.$extra;

				if (variant !== 'action') {
					await onAction?.('forecaster:operations:save', {
						scenario: {
							_id: sourceScenarioData?._id,
							name: sourceScenarioData?.name,
						},
						data: [data],
						noRedirect: true,
					});

					return true;
				}

				const changes = getValues('changes') ?? [];
				setValue('changes', [...changes, data]);
				const allOperations = getValues('allOperations') ?? [];
				setValue('allOperations', [...allOperations, data]);

				return true;
			},
			[onAction, store, inputConfig, getValues, setValue, variant],
		);

		const formOperation = React.useMemo(() => {
			const extra = {};

			if (inputConfig) {
				extra[inputConfig.name] = (operation.payload.links ?? []).find(
					({ type }) => type === inputConfig.linkType,
				)?.id;
			}

			return {
				op: 'REPLACE',
				payload: {
					...operation.payload,
					targetId: operation.uuid ?? operation._id,
				},
				$extra: extra,
			};
		}, [operation, inputConfig]);

		if (removed) {
			return null;
		}

		return (
			<Form defaultValues={formOperation} onSubmit={handleSubmit}>
				<AutoSave debounce={1000} />
				<ItemView
					className={className}
					operation={operation}
					style={style}
					type={type}
					showTags={showTags}
					inputConfig={inputConfig}
					placeholder={placeholder}
					showCategory={showCategory}
					InputFormat={InputFormat}
					handleRemove={handleRemove}
					handleCopy={handleCopy}
					currencySelectorRef={currencySelectorRef}
				/>
			</Form>
		);
	},
	(prevProps, nextProps) => {
		return prevProps.operation._id === nextProps.operation._id;
	},
);

TransactionItemProvider.displayName = 'TransactionItemProvider';

TransactionItemProvider.propTypes = {
	className: PropTypes.string,
	types: PropTypes.arrayOf(PropTypes.string),
	name: PropTypes.string,
	onAction: PropTypes.func,
	showCategory: PropTypes.bool,
};

export default TransactionItemProvider;
